/**
 * Shell autenticado (manual §4, "Navegação").
 *
 * Barra lateral em `--surface`. Item ativo: texto `--text` com indicador `--accent`
 * e fundo sutil `--surface-2`. Itens inativos em `--text-secondary`. Nenhum âmbar
 * aqui — o âmbar é reservado à ação principal de cada tela e ao vínculo aceso no
 * grafo. Um menu inteiro pintado de âmbar mataria a escassez que lhe dá sentido.
 */
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Activity, Brush, Check, Copy, FileSpreadsheet, LayoutDashboard, LogOut, Menu, Monitor, Moon, PanelLeftClose, PanelLeftOpen, SquarePen, Sun, Users, X } from 'lucide-react';
import { type ComponentType, Fragment, useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Lockup, Marca } from '@/components/Logo';
import { ModalCadastro } from '@/components/ModalCadastro';
import { Chip, Separator } from '@/components/ui/misc';
import { api, type PreferenciasUsuario } from '@/lib/api';
import { useSessao } from '@/lib/auth';
import { listaRecursos } from '@/lib/recursos';
import { type Escolha, useTema } from '@/lib/tema';
import { BarraProgresso } from '@/components/ui/barraProgresso';
import { cn } from '@/lib/utils';

type Item = {
  rotulo: string;
  para: string;
  icone: ComponentType<{ className?: string }>;
  /** Marcado quando a tela ainda não existe. Um link morto mente; um rótulo honesto não. */
  fase?: string;
};

// O menu das entidades de cadastro é DERIVADO de RECURSOS — registrar um descriptor
// já o coloca aqui. Início, Duplicatas e Usuários são fixos (não são CRUD de cadastro).
//
// Descriptors com `grupo` descem para um submenu recolhível: telefone, e-mail e endereço são MEIO,
// não fim — ninguém abre o sistema para ver uma lista de telefones —, e no mesmo nível das
// entidades eles empurrariam Pessoas e Empresas para baixo da dobra.
const item = (r: ReturnType<typeof listaRecursos>[number]): Item => ({
  rotulo: r.titulo,
  para: `/cadastro/${r.chave}`,
  icone: r.icone,
});

const NAV: Item[] = [
  { rotulo: 'Início', para: '/', icone: LayoutDashboard },
  ...listaRecursos()
    .filter((r) => !r.grupo)
    .map(item),
  { rotulo: 'Importar planilha', para: '/cadastro/importar-global', icone: FileSpreadsheet },
  { rotulo: 'Duplicatas', para: '/cadastro/duplicatas', icone: Copy },
  { rotulo: 'Jobs', para: '/jobs', icone: Activity },
  { rotulo: 'Sanitizar Dados', para: '/sanitizar', icone: Brush },
  { rotulo: 'Usuários', para: '/usuarios', icone: Users },
];

/** Rótulo do grupo → itens, derivado do mesmo registro (nada de lista paralela).
 *
 * Calculado sob demanda, e NÃO no topo do módulo: `listaRecursos()` lê o registro, que importa os
 * descritores, que importam painéis, que importam daqui de volta. Avaliar no topo fechava o ciclo
 * cedo demais e a aplicação subia com `Cannot access 'RECURSOS' before initialization` — tela
 * branca, sem pista nenhuma na interface. */
function grupos(): Record<string, Item[]> {
  return listaRecursos()
    .filter((r) => r.grupo)
    .reduce<Record<string, Item[]>>((acc, r) => {
      (acc[r.grupo as string] ??= []).push(item(r));
      return acc;
    }, {});
}

const linkClass = (ativo: boolean, colap = false) =>
  cn(
    'relative flex items-center gap-3 rounded-md py-2.5 text-[15px]',
    colap ? 'justify-center px-0' : 'px-3',
    'transition-colors duration-[var(--dur-fast)] ease-[var(--ease)]',
    'outline-none focus-visible:shadow-[var(--focus-ring)]',
    ativo
      ? // O item ativo é ÂMBAR: fundo translúcido na cor da marca, texto e ícone acesos, e a barra
        // de 3px à esquerda. Antes o fundo era um cinza (`--surface-2`) e só a barrinha marcava —
        // num menu de catorze itens, três pixels de cor é pouco para responder "onde eu estou?".
        'bg-[color-mix(in_oklab,var(--primary)_13%,transparent)] font-medium text-text before:absolute before:left-0 before:top-1/2 before:h-5 before:w-[3px] before:-translate-y-1/2 before:rounded-pill before:bg-primary [&_svg]:text-primary'
      : 'text-text-secondary hover:bg-surface-hover hover:text-text',
  );

function iniciais(nome: string) {
  return nome
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join('');
}

export function AppShell() {
  const { usuario, sair, recarregar } = useSessao();
  const { escolha, efetivo, definir } = useTema();
  const navegar = useNavigate();
  const local = useLocation();
  const [drawer, setDrawer] = useState(false);
  const [colapsada, setColapsada] = useState(false);
  const [modalCadastro, setModalCadastro] = useState(false);

  // Conta → interface: ao carregar o usuário, aplica o tema e a sidebar salvos. Roda quando o
  // usuário muda (login/recarregar), não a cada troca manual — para não brigar com o operador.
  useEffect(() => {
    const p = usuario?.preferencias;
    if (!p) return;
    if (p.tema && p.tema !== escolha) definir(p.tema);
    if (typeof p.sidebar_colapsada === 'boolean') setColapsada(p.sidebar_colapsada);
  }, [usuario?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  function salvarPref(parcial: PreferenciasUsuario) {
    void api.patch('/auth/eu', { preferencias: parcial });
  }
  function mudarTema(e: Escolha) {
    definir(e);
    salvarPref({ tema: e });
  }
  function alternarColapsada() {
    const nova = !colapsada;
    setColapsada(nova);
    salvarPref({ sidebar_colapsada: nova });
  }

  useEffect(() => setDrawer(false), [local.pathname]);
  useEffect(() => {
    document.body.style.overflow = drawer ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [drawer]);

  const atual = NAV.find((i) => i.para === local.pathname);
  const titulo = atual?.rotulo ?? 'bHunter';

  const corpoNav = (colap: boolean) => (
    <>
      <div className={cn('mb-6 pt-1', colap ? 'flex justify-center px-0' : 'px-2')}>
        {colap ? <Marca /> : <Lockup />}
      </div>
      <nav className="flex flex-col gap-1">
        {NAV.map((item) => {
          const Icone = item.icone;
          const conteudo = item.fase ? (
            <div
              className={cn(
                'flex cursor-not-allowed items-center gap-3 rounded-md py-2.5 text-[15px] text-text-tertiary opacity-45',
                colap ? 'justify-center px-0' : 'px-3',
              )}
              title={colap ? item.rotulo : `Disponível na ${item.fase}`}
            >
              <Icone className="size-[18px] shrink-0" />
              {!colap && (
                <>
                  <span className="flex-1">{item.rotulo}</span>
                  <Chip>{item.fase}</Chip>
                </>
              )}
            </div>
          ) : (
            <NavLink
              to={item.para}
              end
              title={colap ? item.rotulo : undefined}
              className={({ isActive }) => linkClass(isActive, colap)}
            >
              <Icone className="size-[18px] shrink-0" />
              {!colap && item.rotulo}
            </NavLink>
          );
          return <Fragment key={item.para}>{conteudo}</Fragment>;
        })}

        {/* Submenus. Recolhido por padrão e aberto sozinho quando a rota atual está dentro dele —
            quem chegou por link não precisa descobrir onde estava. Some com a barra colapsada:
            um accordion de 16px de largura não é navegação, é enigma. */}
        {!colap &&
          Object.entries(grupos()).map(([grupo, itens]) => {
            const dentro = itens.some((i) => i.para === local.pathname);
            return (
              <details key={grupo} open={dentro} className="mt-2">
                <summary className="tipo-rotulo cursor-pointer select-none px-3 py-2 text-text-tertiary hover:text-text">
                  {grupo}
                </summary>
                <div className="mt-1 flex flex-col gap-1 border-l border-border pl-2">
                  {itens.map((i) => {
                    const IconeGrupo = i.icone;
                    return (
                      <NavLink
                        key={i.para}
                        to={i.para}
                        end
                        className={({ isActive }) => linkClass(isActive, false)}
                      >
                        <IconeGrupo className="size-[18px] shrink-0" />
                        {i.rotulo}
                      </NavLink>
                    );
                  })}
                </div>
              </details>
            );
          })}
      </nav>
    </>
  );

  return (
    <div className="flex h-dvh overflow-hidden bg-bg text-text">
      {/* Uma vez, para o produto inteiro: lê o contador de requisições do cliente HTTP. */}
      <BarraProgresso />
      <aside
        className={cn(
          'hidden h-full shrink-0 flex-col gap-1 overflow-y-auto border-r border-border bg-surface md:flex',
          colapsada ? 'w-16 px-2 py-4' : 'w-60 p-4',
        )}
      >
        {corpoNav(colapsada)}
        <button
          type="button"
          onClick={alternarColapsada}
          title={colapsada ? 'Expandir menu' : 'Recolher menu'}
          className={cn(
            'mt-auto flex items-center gap-3 rounded-md py-2.5 text-[15px] text-text-secondary outline-none transition-colors hover:bg-surface-hover hover:text-text focus-visible:shadow-[var(--focus-ring)]',
            colapsada ? 'justify-center px-0' : 'px-3',
          )}
        >
          {colapsada ? (
            <PanelLeftOpen className="size-[18px] shrink-0" />
          ) : (
            <>
              <PanelLeftClose className="size-[18px] shrink-0" />
              Recolher
            </>
          )}
        </button>
      </aside>

      {drawer && (
        <>
          <div
            className="fixed inset-0 z-[var(--z-backdrop)] bg-[rgba(13,16,23,.6)] backdrop-blur-sm md:hidden"
            onClick={() => setDrawer(false)}
          />
          <aside className="fixed inset-y-0 left-0 z-[var(--z-modal)] flex w-72 max-w-[85%] flex-col gap-1 overflow-y-auto border-r border-border bg-surface p-4 md:hidden">
            <button
              className="absolute right-3 top-3 rounded-md p-2 text-text-secondary outline-none focus-visible:shadow-[var(--focus-ring)]"
              onClick={() => setDrawer(false)}
              aria-label="Fechar menu"
            >
              <X className="size-5" />
            </button>
            {corpoNav(false)}
          </aside>
        </>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-[var(--z-sticky)] flex items-center justify-between border-b border-border bg-surface px-4 py-3 md:px-6">
          <div className="flex items-center gap-3">
            <button
              className="rounded-md p-2 outline-none focus-visible:shadow-[var(--focus-ring)] md:hidden"
              onClick={() => setDrawer(true)}
              aria-label="Abrir menu"
            >
              <Menu className="size-5" />
            </button>
            <span className="tipo-rotulo">{titulo}</span>
          </div>

          <div className="flex items-center gap-1">
            <DropdownMenu.Root>
              {/* O ícone mostra o tema EFETIVO no momento (mesmo se a escolha for "Sistema"). */}
              <DropdownMenu.Trigger
                aria-label="Tema"
                className="rounded-md p-2 text-text-secondary outline-none transition-colors duration-[var(--dur-fast)] hover:bg-surface-hover hover:text-text focus-visible:shadow-[var(--focus-ring)]"
              >
                {efetivo === 'dark' ? (
                  <Moon className="size-[18px]" />
                ) : (
                  <Sun className="size-[18px]" />
                )}
              </DropdownMenu.Trigger>
              <DropdownMenu.Portal>
                <DropdownMenu.Content
                  align="end"
                  sideOffset={8}
                  className="z-[var(--z-dropdown)] min-w-44 rounded-lg border border-border bg-surface p-1 text-text shadow-[var(--shadow-md)]"
                >
                  {(
                    [
                      ['light', 'Claro', Sun],
                      ['dark', 'Escuro', Moon],
                      ['system', 'Sistema', Monitor],
                    ] as const
                  ).map(([val, rot, Ic]) => (
                    <DropdownMenu.Item
                      key={val}
                      className="flex cursor-pointer items-center gap-2 rounded-sm px-3 py-2 text-[15px] outline-none data-[highlighted]:bg-surface-hover"
                      onSelect={() => mudarTema(val)}
                    >
                      <Ic className="size-4" />
                      <span className="flex-1">{rot}</span>
                      {escolha === val && <Check className="size-4 text-accent" />}
                    </DropdownMenu.Item>
                  ))}
                </DropdownMenu.Content>
              </DropdownMenu.Portal>
            </DropdownMenu.Root>

            <DropdownMenu.Root>
              <DropdownMenu.Trigger className="flex items-center gap-2 rounded-md px-2 py-1.5 text-[15px] outline-none transition-colors duration-[var(--dur-fast)] hover:bg-surface-hover focus-visible:shadow-[var(--focus-ring)]">
                <span className="grid size-8 place-items-center overflow-hidden rounded-pill bg-surface-2 font-mono text-xs text-text-secondary">
                  {usuario?.avatar ? (
                    <img src={usuario.avatar} alt="" className="size-full object-cover" />
                  ) : (
                    iniciais(usuario?.nome ?? '?')
                  )}
                </span>
                <span className="hidden sm:inline">{usuario?.nome.split(' ')[0]}</span>
              </DropdownMenu.Trigger>
              <DropdownMenu.Portal>
                <DropdownMenu.Content
                  align="end"
                  sideOffset={8}
                  className="z-[var(--z-dropdown)] min-w-56 rounded-lg border border-border bg-surface p-1 text-text shadow-[var(--shadow-md)]"
                >
                  <div className="px-3 py-2">
                    <div className="text-[13px]">{usuario?.nome}</div>
                    {/* E-mail é identificador: vai em mono (manual §2). */}
                    <div className="tipo-dado text-text-tertiary">{usuario?.email}</div>
                  </div>
                  <Separator className="my-1" />
                  <DropdownMenu.Item
                    className="flex cursor-pointer items-center gap-2 rounded-sm px-3 py-2 text-[15px] outline-none data-[highlighted]:bg-surface-hover"
                    onSelect={() => setModalCadastro(true)}
                  >
                    <SquarePen className="size-4" />
                    Meu Cadastro
                  </DropdownMenu.Item>
                  <Separator className="my-1" />
                  <DropdownMenu.Item
                    className="flex cursor-pointer items-center gap-2 rounded-sm px-3 py-2 text-[15px] outline-none data-[highlighted]:bg-surface-hover"
                    onSelect={async () => {
                      await sair();
                      navegar('/login', { replace: true });
                    }}
                  >
                    <LogOut className="size-4" />
                    Sair
                  </DropdownMenu.Item>
                </DropdownMenu.Content>
              </DropdownMenu.Portal>
            </DropdownMenu.Root>
          </div>
        </header>

        {/* O scroll vive AQUI (não no documento): assim o header sticky no topo e a barra de ações
            sticky no rodapé grudam nas bordas da viewport, sempre visíveis ao rolar o formulário. */}
        <main className="w-full min-w-0 flex-1 overflow-y-auto overflow-x-clip p-4 md:p-6">
          <Outlet />
        </main>
      </div>

      {usuario && (
        <ModalCadastro
          aberto={modalCadastro}
          onAbrir={setModalCadastro}
          usuario={usuario}
          onSalvo={recarregar}
        />
      )}
    </div>
  );
}
